import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { BoxDashboardPaginatedBlock } from "@/modules/boxDashboard/components/boxDashboardPaginatedBlock"
import { LoadingBlock } from "@/modules/boxDashboard/components/loadingBlock"
import type { FinalRoundType } from "@/modules/boxDashboard/gateway/boxDashboardApiDto"
import {
  FINAL_ROUND_MEMBERS_PAGE_SIZE,
  useFinalRoundMembers,
  useTotalFinalRound,
} from "@/modules/boxDashboard/modules/FinalRoundMembers/hooks/useFinalRoundMembers"
import {
  createFinalRoundMembersBlockModel,
  type FinalRoundMemberModel,
} from "@/modules/boxDashboard/modules/FinalRoundMembers/models/finalRoundMembersModel"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import attendeesEmptyIllustration from "@images/empty/attendees_empty.svg"
import { word } from "@modules/core/utils/i18n"
import { cn } from "@modules/lib/utils"
import { useMemberSideBar } from "@modules/members/hooks/useMemberSidebar"
import { CircleHelp } from "lucide-react"
import { startTransition, useState } from "react"

const FILTER_OPTIONS: {
  value: FinalRoundType | undefined
  label: string
}[] = [
  { value: undefined, label: word("boxDashboard.finalRound.filter.all") },
  { value: "CLASS_PASS", label: word("boxDashboard.finalRound.filter.classPass") },
  { value: "MEMBERSHIP", label: word("boxDashboard.finalRound.filter.membership") },
]

export const FinalRoundMembersBlock = () => {
  const [currentPage, setCurrentPage] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState<FinalRoundType | undefined>(undefined)

  const { members, totalCount, isFetching } = useFinalRoundMembers(selectedFilter, currentPage)
  const { total, classPassTotal, membershipTotal } = useTotalFinalRound()
  const { navigateToMemberDetailsInfo } = useMemberSideBar()

  const model = createFinalRoundMembersBlockModel({
    members,
    totalCount,
    currentPage,
    pageSize: FINAL_ROUND_MEMBERS_PAGE_SIZE,
    isExpanded,
  })

  const handleMemberClick = (memberId: string) => {
    navigateToMemberDetailsInfo(memberId, {})
  }

  const handleFilterChange = (filter: FinalRoundType | undefined) => {
    startTransition(() => {
      setSelectedFilter(filter)
      setCurrentPage(0)
      setIsExpanded(false)
    })
  }

  const handleShowMore = () => {
    setIsExpanded(true)
  }

  const handleShowLess = () => {
    startTransition(() => {
      setIsExpanded(false)
      setCurrentPage(0)
    })
  }

  const getFilterCount = (filter: FinalRoundType | undefined): number => {
    if (filter === undefined) return total
    if (filter === "CLASS_PASS") return classPassTotal
    return membershipTotal
  }

  const header = (
    <div className="flex shrink-0 flex-col gap-1 pt-6 pr-4 pb-0 pl-6">
      <div className="flex items-center gap-2">
        <Text fontStyle="HM" className="text-textDefault-heading">
          {word("boxDashboard.finalRound.title")}
        </Text>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={<CircleHelp className="size-6 text-textOnSurface-subtle hover:text-neutral-500" />}
            />
            <TooltipContent side="bottom" align="start" className="max-w-60 flex-col rounded-xl p-0 shadow-lg">
              <div className="flex w-full items-center gap-1 border-b border-neutral-300 p-4">
                <CircleHelp className="size-6 text-textOnSurface-subtle" />
                <Text fontStyle="LM">{word("boxDashboard.finalRound.title")}</Text>
              </div>
              <div className="p-4 pt-2">
                <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces">
                  {word("boxDashboard.finalRound.tooltip")}
                </Text>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <Text fontStyle="BM" className="text-textOnSurface-muted">
        {word("boxDashboard.finalRound.subtitle")}
      </Text>
      <div className="flex items-center gap-4 pt-1">
        {FILTER_OPTIONS.map((option) => (
          <button
            key={option.label}
            onClick={() => {
              handleFilterChange(option.value)
            }}
            className={cn(
              "flex cursor-pointer items-center gap-1.5 rounded-full p-1 px-2 transition-colors",
              option.value === selectedFilter
                ? "bg-surface-background-float text-textOnSurface-default"
                : "border-transparent text-textOnSurface-subtle hover:text-textOnSurface-muted",
            )}
          >
            <Text fontStyle="BM">{option.label}</Text>
            <div
              className={cn(
                "flex items-center justify-center rounded-full bg-surface-background-float px-1.5",
                option.value === selectedFilter ? "bg-interactive-background-neutral-faded-highlighted" : "",
              )}
            >
              <Text
                fontStyle="BXS"
                className={cn(
                  "text-textOnSurface-muted",
                  option.value === selectedFilter ? "text-textOnSurface-default" : "",
                )}
              >
                {getFilterCount(option.value)}
              </Text>
            </div>
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <BoxDashboardPaginatedBlock
      header={header}
      isEmpty={model.isEmpty}
      isLoading={isFetching}
      loadingComponent={<LoadingBlock className="mx-6 mt-6 h-90" />}
      emptyState={<EmptyState />}
      isExpanded={isExpanded}
      showShowMore={model.showShowMore}
      showShowLess={model.showShowLess}
      showPagination={model.showPagination}
      onShowMore={handleShowMore}
      onShowLess={handleShowLess}
      totalPages={model.totalPages}
      currentPage={currentPage}
      onPageChange={(page) => {
        startTransition(() => {
          setCurrentPage(page)
        })
      }}
      collapsedHeight="h-139"
      expandedHeight="h-208"
    >
      <div className="grid grid-cols-2 gap-x-1 gap-y-3 px-6">
        {model.members.map((member) => (
          <FinalRoundCard
            key={member.id}
            member={member}
            onClick={() => {
              handleMemberClick(member.memberId)
            }}
          />
        ))}
      </div>
    </BoxDashboardPaginatedBlock>
  )
}

const EmptyState = () => {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2">
      <img src={attendeesEmptyIllustration} alt="" className="h-43.75 w-74.5" />
      <Text fontStyle="LL" className="text-center">
        {word("boxDashboard.finalRound.empty.title")}
      </Text>
    </div>
  )
}

const FinalRoundCard = ({ member, onClick }: { member: FinalRoundMemberModel; onClick: () => void }) => {
  return (
    <button
      className="flex min-w-45 cursor-pointer flex-col overflow-hidden rounded-xl border-2 border-surface-foreground bg-surface-foreground text-left hover:border-interactive-background-neutral-highlighted"
      onClick={onClick}
    >
      {/* Top section: Avatar, Name, Offer, Badge */}
      <div className="flex w-full items-center justify-between bg-linear-to-r from-(--color-red-900) from-0% to-(--color-red-800) to-150% p-2">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <ProfileAvatar className="size-6 shrink-0" profile={{ avatar: member.avatar, username: member.fullName }} />
          <div className="flex min-w-0 flex-1 flex-col">
            <Text fontStyle="LM" className="truncate">
              {member.fullName}
            </Text>
            <Text fontStyle="LS" className="truncate text-textOnSurface-muted">
              {member.offerName ? member.offerName : "\u00A0"}
            </Text>
          </div>
        </div>
        <div className="flex items-center justify-center rounded-full bg-interactive-background-neutral-faded-highlighted px-1.5">
          <Text fontStyle="BXS" className="">
            {member.badgeLabel}
          </Text>
        </div>
      </div>

      {/* Bottom section: End date and Sessions info */}
      <div className="flex w-full gap-1 bg-surface-background p-2">
        <div className="flex flex-1 flex-col">
          <Text fontStyle="LXS" className="text-textOnSurface-muted">
            {word("boxDashboard.finalRound.endSubscription")}
          </Text>
          <Text fontStyle="BXS" className="text-textOnSurface-subtle">
            {member.endDate}
          </Text>
        </div>
        <div className="flex flex-1 flex-col items-end">
          <Text fontStyle="LXS" className="text-textOnSurface-muted">
            {word("boxDashboard.finalRound.sessionsLast30Days")}
          </Text>
          <Text fontStyle="BXS" className="text-textOnSurface-subtle">
            {member.sessionsLast30Days}
          </Text>
        </div>
      </div>
    </button>
  )
}
